import { Link, useLocation } from "react-router-dom";
import { WifiOff, NotebookPen } from "lucide-react";
import { LangSwitch } from "@/components/LangSwitch";
import { useApp } from "@/context/AppContext";
import { Logo, Wordmark } from "@/components/Logo";
import SaasScene from "@/components/SaasScene";
import {
  Separator, ModulesSection, DiffSection,
  LandingFooter, BackToTop, scrollToId,
} from "@/components/landing/Sections";

const NAV = [["modules", "nav_modules"]];

const FEATURES = [
  { icon: WifiOff, title: "feat_offline", desc: "feat_offline_desc" },
  { icon: NotebookPen, title: "feat_credit", desc: "feat_credit_desc" },
];

function Header() {
  const { t } = useApp();
  const onLogin = useLocation().pathname === "/login";
  return (
    <header className="sticky top-0 z-30 bg-[#0B2A5B]/95 backdrop-blur-md text-white border-b border-white/10">
      <div className="max-w-6xl mx-auto px-5 h-[72px] flex items-center gap-4">
        <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-3" data-testid="auth-brand-button">
          <Logo className="w-11 h-11" />
          <Wordmark className="orbit-wordmark-anim hidden min-[400px]:inline-flex" size="sm" />
        </button>
        <nav className="hidden md:flex items-center gap-1 ms-4" data-testid="landing-nav">
          {NAV.map(([id, k]) => (
            <button key={id} type="button" onClick={() => scrollToId(id)} data-testid={`landing-nav-${id}`}
              className="nav-link px-3 h-10 text-sm font-medium uppercase tracking-wide text-white/85 hover:text-white">{t(k)}</button>
          ))}
        </nav>
        <div className="ms-auto flex items-center gap-3">
          <LangSwitch compact />
          <Link to={onLogin ? "/register" : "/login"} data-testid="auth-header-cta"
            className="h-11 px-4 sm:px-6 rounded-full orbit-gold-btn text-[#1A1A1A] grid place-items-center text-sm font-bold whitespace-nowrap">
            {onLogin ? t("create_shop") : t("login")}
          </Link>
        </div>
      </div>
    </header>
  );
}

export default function AuthLayout({ title, subtitle, children }) {
  const { t } = useApp();
  return (
    <div className="min-h-screen flex flex-col bg-background overflow-x-clip">
      <Header />
      <main className="flex-1">
        <section className="sky-bg text-white overflow-hidden relative" data-testid="landing-hero">
          <div className="sky-stars" aria-hidden="true" /><div className="sky-stars sky-stars-2" aria-hidden="true" /><div className="sky-horizon" aria-hidden="true" />
          <div className="relative max-w-6xl mx-auto px-5 py-10 lg:py-16 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            <section className="order-2 lg:order-1 rise flex flex-col justify-center">
              <h2 className="font-heading text-3xl sm:text-4xl uppercase leading-[1.1] hidden lg:block">{t("hero_title")}</h2>
              <div className="hidden lg:block"><Separator /></div>
              <div className="relative h-[360px] sm:h-[470px] lg:h-[540px] grid place-items-center"><SaasScene className="scale-[.68] sm:scale-[.9] lg:scale-100 origin-center" /></div>
              <ul className="mt-4 grid grid-cols-2 gap-4 max-w-md">
                {FEATURES.map(({ icon: Icon, title: ft, desc }) => (
                  <li key={ft} className="flex flex-col gap-2" title={t(desc)}>
                    <span className="w-11 h-11 rounded-full bg-white/15 grid place-items-center"><Icon className="w-5 h-5" /></span>
                    <div className="font-bold leading-tight text-base sm:text-lg">{t(ft)}</div>
                  </li>
                ))}
              </ul>
            </section>
            <section className="order-1 lg:order-2 w-full max-w-md mx-auto lg:mx-0 lg:justify-self-end lg:self-center bg-white text-foreground rounded-2xl p-6 sm:p-8 shadow-2xl shadow-black/30 rise">
              <h1 className="font-heading text-3xl sm:text-4xl text-center text-[#0B2A5B]">{title}</h1>
              <p className="text-muted-foreground mt-1 mb-6 text-center">{subtitle}</p>
              {children}
            </section>
          </div>
        </section>
        <ModulesSection />
        <DiffSection />
      </main>
      <LandingFooter />
      <BackToTop />
    </div>
  );
}
